// Stepping through a card's group of warnings (‹ 1 / 3 ›, PAGER-SPEC.md). A
// card stands for every warning of one kind on the route — hazards of one
// type, difficult sections of one SAC grade — and shows the first of them
// along the route. The pager walks the same group in the same order, so its
// first page is the card itself.
//
// The order is the server's (RouteAlerts::Alert#route_order_key): nearest the
// start of the route first, unknown positions last, then nearest the line,
// then id. Pure, so every rule is a unit test.

import type { RouteAlert } from './routeAlertsModel';
import type { Translate } from '../trailHazards/hazardDetails';
import { hazardTypeName } from '../trailHazards/hazardDetails';
import { sacGradeName } from './sacSections';

/** The server's group key (RouteAlerts::Alert#group_key): "hazard:tree_down", "sac:4". */
export function alertGroupKey(alert: RouteAlert): string {
  if (alert.kind === 'hazard' && alert.hazard?.rawHazardType) return `hazard:${alert.hazard.rawHazardType}`;
  if (alert.kind === 'difficult_terrain' && alert.sacGrade !== null) return `sac:${alert.sacGrade}`;
  return `${alert.rawKind}:${alert.id}`;
}

/** Route order: along the route (unknown last), then nearest the line, then id. */
export function compareRouteOrder(a: RouteAlert, b: RouteAlert): number {
  const aUnplaced = a.distanceAlongRouteM === null ? 1 : 0;
  const bUnplaced = b.distanceAlongRouteM === null ? 1 : 0;
  if (aUnplaced !== bUnplaced) return aUnplaced - bUnplaced;
  const along = (a.distanceAlongRouteM ?? 0) - (b.distanceAlongRouteM ?? 0);
  if (along !== 0) return along;
  const from = (a.distanceFromRouteM ?? 0) - (b.distanceFromRouteM ?? 0);
  if (from !== 0) return from;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/** Every warning of the group, in route order (ties keep the list's order). */
export function groupMembers(alerts: RouteAlert[], groupKey: string): RouteAlert[] {
  return (alerts ?? [])
    .map((alert, index) => ({ alert, index }))
    .filter(({ alert }) => alertGroupKey(alert) === groupKey)
    .sort((x, y) => compareRouteOrder(x.alert, y.alert) || x.index - y.index)
    .map(({ alert }) => alert);
}

export interface RouteAlertPager {
  groupKey: string;
  /** The group, in route order. */
  members: RouteAlert[];
  /** The one being shown. */
  index: number;
}

export type PagerDirection = 'next' | 'previous';

/**
 * The pager for `current` among `alerts`: null when its group is just itself
 * (a single warning shows exactly as before, with no pager).
 */
export function pagerFor(alerts: RouteAlert[], current: RouteAlert): RouteAlertPager | null {
  const groupKey = alertGroupKey(current);
  const members = groupMembers(alerts, groupKey);
  if (members.length < 2) return null;
  const index = members.findIndex((alert) => alert.id === current.id);
  return index < 0 ? null : { groupKey, members, index };
}

export function canGoPrevious(pager: RouteAlertPager | null): boolean {
  return !!pager && pager.index > 0;
}

export function canGoNext(pager: RouteAlertPager | null): boolean {
  return !!pager && pager.index < pager.members.length - 1;
}

/** One step, or null at either end: there is no wrap-around. */
export function stepPager(pager: RouteAlertPager | null, direction: PagerDirection): RouteAlertPager | null {
  if (!pager) return null;
  const index = pager.index + (direction === 'next' ? 1 : -1);
  if (index < 0 || index >= pager.members.length) return null;
  return { ...pager, index };
}

export function currentAlert(pager: RouteAlertPager | null): RouteAlert | null {
  return pager ? pager.members[pager.index] ?? null : null;
}

/**
 * The pager after the list was fetched again: the same alert in its group's
 * new order, or null when it is gone or now stands alone.
 */
export function refreshPager(pager: RouteAlertPager | null, alerts: RouteAlert[]): RouteAlertPager | null {
  const current = currentAlert(pager);
  if (!current) return null;
  const fresh = alerts.find((alert) => alert.id === current.id);
  return fresh ? pagerFor(alerts, fresh) : null;
}

/** What the pager bar and the section callout's arrows draw. */
export interface PagerView {
  /** "1 / 3". */
  countText: string;
  /** "Tree down, 1 of 3 on this route": the count's accessible name. */
  positionLabel: string;
  previousLabel: string;
  nextLabel: string;
  canPrevious: boolean;
  canNext: boolean;
}

/** The name the position is read out with: the hazard type, or the section's grade name. */
export function pagerItemName(alert: RouteAlert, t: Translate): string {
  if (alert.kind === 'hazard' && alert.hazard?.hazardType) return hazardTypeName(alert.hazard.hazardType, t);
  if (alert.kind === 'difficult_terrain' && alert.sacGrade !== null) return sacGradeName(alert.sacGrade, t);
  return t('route_alerts.generic_warning');
}

export function pagerView(pager: RouteAlertPager | null, t: Translate): PagerView | null {
  const current = currentAlert(pager);
  if (!pager || !current) return null;
  const values = { index: pager.index + 1, total: pager.members.length };
  return {
    countText: t('route_alerts.pager_count', values),
    positionLabel: `${pagerItemName(current, t)}, ${t('route_alerts.pager_position', values)}`,
    previousLabel: t('route_alerts.pager_previous'),
    nextLabel: t('route_alerts.pager_next'),
    canPrevious: canGoPrevious(pager),
    canNext: canGoNext(pager),
  };
}
